import { ChartDataState, TooltipState } from "../store/chart";
import { UIButton, UIColor, UIFont, UIImage, UIText, UIToggle } from "../types";
import { FontWeight, FontFamily } from "../types/fonts";
import { v4 as uuid } from "uuid";

export const createMockedText = ({
  text,
  color,
  font,
}: {
  text: string;
  color?: string;
  font?: UIFont;
}): UIText => {
  return {
    value: text,
    color: color
      ? {
          hex: color,
        }
      : undefined,
    font: font && {
      family: font.family || FontFamily.HarmoniaSansStd,
      weight: font.weight || FontWeight.Normal,
    },
  };
};

export const mockText = (text: string) => createMockedText({ text });

export const createMockedButton = ({
  text,
  link,
  font,
}: {
  text: string;
  font?: UIFont;
  link?: string;
}): UIButton => {
  return {
    label: {
      value: text,
      font: font && {
        family: font.family || FontFamily.HarmoniaSansStd,
        weight: font.weight || FontWeight.Normal,
      },
    },
    link,
  };
};

export const createMockToggle = ({
  text,
  font,
  offColor,
  onColor,
}: {
  text: string;
  font?: UIFont;
  offColor: string;
  onColor: string;
}): UIToggle => {
  return {
    label: {
      value: text,
      font: font && {
        family: font.family || FontFamily.HarmoniaSansStd,
        weight: font.weight || FontWeight.Normal,
      },
    },
    onColor: onColor ? mockColor(onColor) : undefined,
    offColor: offColor ? mockColor(offColor) : undefined,
  };
};

export const mockImage = (url: string, alt?: string): UIImage => ({ url, alt });
export const mockButton = (text: string) => createMockedButton({ text });
export const mockColor = (hex: string): UIColor => ({ hex });
export const mockToggle = (text: string) => createMockedButton({ text });
export const mockTooltip = (
  visible: boolean,
  description?: string,
): TooltipState => ({
  description:
    description ||
    "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco.",
  visible,
});

export const CHART_MOCKED_API_RESPONSE: ChartDataState = {
  styling: {
    filterButton: {},
    tag: {},
    productApplication: {},
    legalCopy: {},
    column: {
      bundleBackgroundColor: mockColor("#F6F7FA"),
    },
    shopButton: {
      copy: "Shop",
    },
    shopSelectButton: {},
  },
  backgroundColor: {
    hex: "",
  },
  header: mockText("Choose products to see how features stack up"),
  useCustomProductFilters: true,
  showAdditionalFilterButton: true,
  productKindFilters: [
    {
      id: "vacuums-filter-uuid",
      productKindId: "vacuums-uuid",
      filterButtonCopy: "Vacuums",
      categoryTitle: "Robot Vacuums",
    },
    {
      id: "mops-filter-uuid",
      productKindId: "mops-uuid",
      filterButtonCopy: "Mops",
      categoryTitle: "Robot Mop",
    },
    {
      id: "2-in-1-filter-uuid",
      productKindId: "2-in-1-uuid",
      filterButtonCopy: "2-in-1 Vacuum & Mop",
      categoryTitle: "2-in-1 Vacuum & Robot Mop",
    },
    {
      id: "bundles-filter-uuid",
      productKindId: "bundles-uuid",
      filterButtonCopy: "Bundles",
      categoryTitle: "Robot Vacuums & Mop Bundles",
    },
  ],
  additionalFilterButton: mockButton("View Air Purifiers"),
  modal: {
    openButton: mockButton("Choose Products"),
    additionalFilterButton: mockButton("View Air Purifiers"),
    showAdditionalFilterButton: true,
    closeButton: mockButton("Back"),
  },
  showOnlyDiffsToggle: mockToggle("Show only differences"),
  showOnlyDiffs: false,
  showPrice: true,
  showShopButton: true,
  showProductApplication: true,
  productApplicationTitle: mockText("Application"),
  showBestFor: true,
  bestForTitle: mockText("Best For"),
  showShopSelectSection: false,
  shopSelectOpenOnLoad: false,
  products: [
    {
      id: "s9-uuid",
      isBundle: false,
      useTag: false,
      productKindId: "vacuums-uuid",
      name: "Roomba® s9 Series",
      price: "$000.00",
      shopUrl: "https://www.google.com",
      useSalesMessage: false,
      application: "Optimized for compact living spaces",
      bestForIcons: [
        mockImage(
          "https://app.irobot.mdsrv.media/public-assets/best-for-icons/larger-homes-icon.svg",
          "Smaller Homes",
        ),
        mockImage(
          "https://app.irobot.mdsrv.media/public-assets/best-for-icons/families-icon.svg",
          "Families",
        ),
        mockImage(
          "https://app.irobot.mdsrv.media/public-assets/best-for-icons/multi-pet-icon.svg",
          "Single Pet",
        ),
      ],
      mobilePreselected: false,
      desktopPreselected: true,
      showShopSelectButton: false,
      data: {
        image: `${process.env.PUBLIC_URL}/assets/images/s9combo.png`,
        features: [
          {
            id: uuid(),
            featureId: "feature.1.1.3",
          },
          {
            id: uuid(),
            featureId: "feature.1.2.1",
          },
          {
            id: uuid(),
            featureId: "feature.1.2.2",
          },
          {
            id: uuid(),
            featureId: "feature.1.2.3",
          },
          {
            id: uuid(),
            featureId: "feature.2.1.1",
          },
          {
            id: uuid(),
            featureId: "feature.2.1.2",
          },
          {
            id: uuid(),
            featureId: "feature.2.1.3",
          },
          {
            id: uuid(),
            featureId: "feature.3.1.1",
            featureItemId: "feature-item.3.1.1.0",
            subItem: "with 2lbs of Activated Carbon",
          },
          {
            id: uuid(),
            featureId: "feature.3.1.2",
            featureItemId: "feature-item.3.1.2.1",
            icon: { url: `${process.env.PUBLIC_URL}/assets/images/10x.webp` },
            itemLegalCopy:
              "Lorem ipsum dolor sit amet, consetetur sadipscing elitr",
          },
          {
            id: uuid(),
            featureId: "feature.4.1.1",
            featureItemId: "feature.4.1.1.1",
            item: "35-65db",
            subItem: "Whisper quiet noise",
          },
          {
            id: uuid(),
            featureId: "feature.4.1.2",
            featureItemId: "feature-item.4.1.2.1",
            item: "11 x 19 x 11 in",
          },
          {
            id: uuid(),
            featureId: "feature.4.1.3",
            featureItemId: "feature-item.4.1.3.1",
            item: "30%* Wider Rubber Brushes",
            itemLegalCopy: "Test feature-item legal copy",
          },
        ],
      },
    },
    {
      id: "j7-uuid",
      isBundle: false,
      productKindId: "vacuums-uuid",
      name: "Roomba® j7 Series",
      price: "$000.00",
      shopUrl: "https://www.google.com",
      useTag: false,
      useSalesMessage: false,
      application: "Ideal for homes with pets",
      bestForIcons: [
        mockImage(
          "https://app.irobot.mdsrv.media/public-assets/best-for-icons/families-icon.svg",
          "Families",
        ),
        mockImage(
          "https://app.irobot.mdsrv.media/public-assets/best-for-icons/smaller-homes-icon.svg",
          "Smaller Homes",
        ),
        mockImage(
          "https://app.irobot.mdsrv.media/public-assets/best-for-icons/single-pet-icon.svg",
          "Single Pet",
        ),
      ],
      mobilePreselected: false,
      desktopPreselected: false,
      showShopSelectButton: false,
      data: {
        image: `${process.env.PUBLIC_URL}/assets/images/stingray.png`,
        features: [],
      },
    },
    {
      id: "i3-uuid",
      isBundle: false,
      productKindId: "vacuums-uuid",
      name: "Roomba® i3 EVO Series",
      price: "$000.00",
      shopUrl: "https://www.google.com",
      useTag: false,
      useSalesMessage: false,
      application: "Ideal for large homes",
      bestForIcons: [
        mockImage(
          "https://app.irobot.mdsrv.media/public-assets/best-for-icons/families-icon.svg",
          "Families",
        ),
        mockImage(
          "https://app.irobot.mdsrv.media/public-assets/best-for-icons/smaller-homes-icon.svg",
          "Smaller Homes",
        ),
        mockImage(
          "https://app.irobot.mdsrv.media/public-assets/best-for-icons/single-pet-icon.svg",
          "Single Pet",
        ),
      ],
      mobilePreselected: false,
      desktopPreselected: false,
      showShopSelectButton: false,
      data: {
        image: `${process.env.PUBLIC_URL}/assets/images/i3combo.png`,
        features: [],
      },
    },
    {
      id: "m6-uuid",
      isBundle: false,
      productKindId: "mops-uuid",
      name: "Braava jet® m6",
      price: "$000.00",
      shopUrl: "https://www.google.com",
      useTag: false,
      useSalesMessage: false,
      application: "Optimized for compact living spaces",
      bestForIcons: [
        mockImage(
          "https://app.irobot.mdsrv.media/public-assets/best-for-icons/families-icon.svg",
          "Families",
        ),
        mockImage(
          "https://app.irobot.mdsrv.media/public-assets/best-for-icons/smaller-homes-icon.svg",
          "Smaller Homes",
        ),
        mockImage(
          "https://app.irobot.mdsrv.media/public-assets/best-for-icons/single-pet-icon.svg",
          "Single Pet",
        ),
      ],
      mobilePreselected: false,
      desktopPreselected: false,
      showShopSelectButton: false,
      data: {
        image: `${process.env.PUBLIC_URL}/assets/images/m6goldstudio.png`,
        features: [],
      },
    },
    {
      id: "combo-j7-uuid",
      isBundle: false,
      productKindId: "2-in-1-uuid",
      name: "Roomba Combo® j7 Series",
      price: "$000.00",
      savePrice: "$000.00",
      shopUrl: "https://www.google.com",
      useTag: true,
      tag: {
        text: createMockedText({ text: "NEW 2-IN-1", color: "#ffffff" }),
        background: mockColor("#AE72A3"),
      },
      useSalesMessage: false,
      application: "Ideal for homes with pets",
      bestForIcons: [
        mockImage(
          "https://app.irobot.mdsrv.media/public-assets/best-for-icons/larger-homes-icon.svg",
          "Smaller Homes",
        ),
        mockImage(
          "https://app.irobot.mdsrv.media/public-assets/best-for-icons/families-icon.svg",
          "Families",
        ),
        mockImage(
          "https://app.irobot.mdsrv.media/public-assets/best-for-icons/single-pet-icon.svg",
          "Single Pet",
        ),
      ],
      mobilePreselected: true,
      desktopPreselected: true,
      showShopSelectButton: false,
      data: {
        image: `${process.env.PUBLIC_URL}/assets/images/stingray.png`,
        features: [
          {
            id: uuid(),
            featureId: "feature.1.1.2",
          },
          {
            id: uuid(),
            featureId: "feature.1.1.3",
          },
          {
            id: uuid(),
            featureId: "feature.1.2.1",
          },
          {
            id: uuid(),
            featureId: "feature.1.2.2",
          },
          {
            id: uuid(),
            featureId: "feature.1.2.3",
          },
          {
            id: uuid(),
            featureId: "feature.2.1.1",
          },
          {
            id: uuid(),
            featureId: "feature.2.1.2",
          },
          {
            id: uuid(),
            featureId: "feature.2.1.3",
          },
          {
            id: uuid(),
            featureId: "feature.3.1.1",
            featureItemId: "feature-item.3.1.1.1",
            item: "HEPA H13 Filter",
          },
          {
            id: uuid(),
            featureId: "feature.3.1.2",
            featureItemId: "feature-item.3.1.2.1",
            icon: { url: `${process.env.PUBLIC_URL}/assets/images/10x.webp` },
            itemLegalCopy:
              "Lorem ipsum dolor sit amet, consetetur sadipscing elitr",
          },
          {
            id: uuid(),
            featureId: "feature.4.1.1",
            featureItemId: "feature.4.1.1.1",
            item: "35-65db",
            subItem: "Whisper quiet noise",
          },
          {
            id: uuid(),
            featureId: "feature.4.1.2",
            featureItemId: "feature-item.4.1.2.2",
            item: "14.5 x 15.2 x 23.6 in",
          },
          {
            id: uuid(),
            featureId: "feature.4.1.3",
            featureItemId: "feature-item.4.1.3.1",
            item: "Wider Rubber Brushes",
            itemLegalCopy: "Test feature-item legal copy 2",
          },
        ],
      },
    },
    {
      id: "s9+m6-uuid",
      isBundle: true,
      productKindId: "bundles-uuid",
      name: "Roomba® s9+ & Braava® jet m6 Bundle",
      price: "$000.00",
      savePrice: "$000.00",
      shopUrl: "https://www.google.com",
      useTag: true,
      tag: {
        text: createMockedText({ text: "BUNDLE", color: "#ffffff" }),
        background: mockColor("#6A768C"),
      },
      useSalesMessage: false,
      application: "Ideal for large homes",
      bestForIcons: [
        mockImage(
          "https://app.irobot.mdsrv.media/public-assets/best-for-icons/smaller-homes-icon.svg",
          "Smaller Homes",
        ),
        mockImage(
          "https://app.irobot.mdsrv.media/public-assets/best-for-icons/families-icon.svg",
          "Families",
        ),
        mockImage(
          "https://app.irobot.mdsrv.media/public-assets/best-for-icons/single-pet-icon.svg",
          "Single Pet",
        ),
      ],
      mobilePreselected: false,
      desktopPreselected: false,
      showShopSelectButton: false,
      leftData: {
        id: uuid(),
        image: `${process.env.PUBLIC_URL}/assets/images/bundles9.png`,
        features: [],
      },
      rightData: {
        id: uuid(),
        image: `${process.env.PUBLIC_URL}/assets/images/bundlem6.png`,
        features: [],
      },
    },
    {
      id: "j7+m6",
      isBundle: true,
      productKindId: "bundles-uuid",
      name: "Roomba® j7+ & Braava® jet m6 Bundle",
      price: "$000.00",
      savePrice: "$000.00",
      shopUrl: "https://www.google.com",
      useTag: true,
      tag: {
        text: createMockedText({ text: "BUNDLE", color: "#ffffff" }),
        background: mockColor("#6A768C"),
      },
      useSalesMessage: false,
      application: "Optimized for compact living spaces",
      bestForIcons: [
        mockImage(
          "https://app.irobot.mdsrv.media/public-assets/best-for-icons/smaller-homes-icon.svg",
          "Smaller Homes",
        ),
        mockImage(
          "https://app.irobot.mdsrv.media/public-assets/best-for-icons/families-icon.svg",
          "Families",
        ),
        mockImage(
          "https://app.irobot.mdsrv.media/public-assets/best-for-icons/single-pet-icon.svg",
          "Single Pet",
        ),
      ],
      mobilePreselected: true,
      desktopPreselected: true,
      showShopSelectButton: false,
      leftData: {
        id: uuid(),
        image: `${process.env.PUBLIC_URL}/assets/images/bundlej7.png`,
        features: [
          {
            id: uuid(),
            featureId: "feature.1.1.3",
          },
          {
            id: uuid(),
            featureId: "feature.1.2.1",
          },
          {
            id: uuid(),
            featureId: "feature.1.2.2",
          },
          {
            id: uuid(),
            featureId: "feature.1.2.3",
          },
          {
            id: uuid(),
            featureId: "feature.2.1.1",
          },
          {
            id: uuid(),
            featureId: "feature.2.1.2",
          },
          {
            id: uuid(),
            featureId: "feature.2.1.3",
          },
          {
            id: uuid(),
            featureId: "feature.3.1.1",
            featureItemId: "feature-item.3.1.1.2",
            item: "HEPA H13 Filter",
            subItem: "with 2lbs of Activated Carbon",
          },
          {
            id: uuid(),
            featureId: "feature.3.1.2",
            featureItemId: "feature-item.3.1.2.1",
            icon: { url: `${process.env.PUBLIC_URL}/assets/images/10x.webp` },
            itemLegalCopy:
              "Lorem ipsum dolor sit amet, consetetur sadipscing elitr",
          },
          {
            id: uuid(),
            featureId: "feature.4.1.1",
            featureItemId: "feature.4.1.1.1",
            item: "35-65db",
            subItem: "whisper quiet noise",
          },
          {
            id: uuid(),
            featureId: "feature.4.1.2",
            featureItemId: "feature-item.4.1.2.3",
            item: "14.56 x 15.23 x 23.62 in",
          },
        ],
      },
      rightData: {
        id: uuid(),
        image: `${process.env.PUBLIC_URL}/assets/images/bundlem6.png`,
        features: [
          {
            id: uuid(),
            featureId: "feature.1.1.2",
          },
          {
            id: uuid(),
            featureId: "feature.1.1.3",
          },
          {
            id: uuid(),
            featureId: "feature.3.1.1",
            featureItemId: "feature-item.3.1.1.2",
            item: "HEPA H13 Filter",
            subItem: "with 2lbs of Activated Carbon",
          },
          {
            id: uuid(),
            featureId: "feature.3.1.2",
            featureItemId: "feature-item.3.1.2.1",
            icon: { url: `${process.env.PUBLIC_URL}/assets/images/10x.webp` },
            itemLegalCopy:
              "Lorem ipsum dolor sit amet, consetetur sadipscing elitr",
          },
          {
            id: uuid(),
            featureId: "feature.4.1.1",
            featureItemId: "feature.4.1.1.2",
            item: "36-66db",
            subItem: "test whisper quiet noise",
          },
          {
            id: uuid(),
            featureId: "feature.4.1.2",
            featureItemId: "feature-item.4.1.2.4",
            item: "10.56 x 10.23 x 13.62 in",
          },
        ],
      },
    },
  ],
  sections: [
    {
      id: "section.1",
      name: "Mapping & Navigation Features",
      collapsed: false,
      showProductOverview: false,
      subSections: [
        {
          id: "subsection.1.1",
          name: "Imprint® Smart Mapping—Hazard Detection & Avoidance",
          features: [
            {
              id: "feature.1.1.1",
              name: "Careful Drive",
              tooltip: mockTooltip(true),
              legalCopy:
                "Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore",
            },
            {
              id: "feature.1.1.2",
              name: "Detects & avoids objects, then adds them to its map",
              tooltip: mockTooltip(true, "Lorem ipsum dolor sit amet"),
            },
            {
              id: "feature.1.1.3",
              name: "Notifies you about hazards in your home and remembers your input",
              tooltip: mockTooltip(true),
              legalCopy: "Lorem ipsum dolor sit amet",
            },
          ],
        },
        {
          id: "subsection.1.2",
          name: "Imprint® Smart Mapping—Targeted Area Cleaning",
          features: [
            {
              id: "feature.1.2.1",
              name: "Creates Clean Zones and Keep Out Zones",
              tooltip: mockTooltip(false),
            },
            {
              id: "feature.1.2.2",
              name: "Customizable Clean Zones within rooms",
              tooltip: mockTooltip(false),
            },
            {
              id: "feature.1.2.3",
              name: "Cleans small areas within rooms at your voice command",
              tooltip: mockTooltip(false),
            },
          ],
        },
      ],
    },
    {
      id: "section.2",
      name: "Pollutant Concerns",
      collapsed: false,
      showProductOverview: false,
      subSections: [
        {
          id: "subsection.2.1",
          features: [
            {
              id: "feature.2.1.1",
              icon: {
                url: `${process.env.PUBLIC_URL}/assets/images/pet-dander.svg`,
              },
              name: "Removes pet dander",
              tooltip: mockTooltip(false),
            },
            {
              id: "feature.2.1.2",
              icon: {
                url: `${process.env.PUBLIC_URL}/assets/images/pollen-dust-asbestos.svg`,
              },
              name: "Removes Pollen, Dust, Asbestos",
              tooltip: mockTooltip(false),
            },
            {
              id: "feature.2.1.3",
              icon: {
                url: `${process.env.PUBLIC_URL}/assets/images/smoke.svg`,
              },
              name: "Removes Smoke",
              tooltip: mockTooltip(false),
            },
          ],
        },
      ],
    },
    {
      id: "section.3",
      name: "Filter & Efficacy",
      collapsed: false,
      showProductOverview: false,
      subSections: [
        {
          id: "section.3.1",
          features: [
            {
              id: "feature.3.1.1",
              icon: {
                url: `${process.env.PUBLIC_URL}/assets/images/filter.svg`,
              },
              name: "Filter",
              tooltip: mockTooltip(false),
            },
            {
              id: "feature.3.1.2",
              icon: {
                url: `${process.env.PUBLIC_URL}/assets/images/filter.svg`,
              },
              name: "Efficiency to 0.1 Microns",
              tooltip: mockTooltip(false),
            },
          ],
        },
      ],
    },
    {
      id: "section.4",
      name: "Specifications",
      collapsed: false,
      showProductOverview: false,
      subSections: [
        {
          id: "section.4.1",
          features: [
            {
              id: "feature.4.1.1",
              name: "Nose",
              tooltip: mockTooltip(false),
            },
            {
              id: "feature.4.1.2",
              name: "Dimensions",
              tooltip: mockTooltip(false),
            },
            {
              id: "feature.4.1.3",
              name: "Feature Item *",
              tooltip: mockTooltip(false),
            },
          ],
        },
      ],
    },
  ],

  showStats: true,
  stats: [
    {
      title: mockText("30-Day Guarantee"),
      description: mockText(
        "Something wrong with your purifier? Tell us within 30 days and return it for a full refund. ",
      ),
      img: mockImage("https://irobot-api-mock.s3.amazonaws.com/calendar.png"),
    },
    {
      title: mockText("Free Shipping"),
      description: mockText(
        "There are absolutely no hidden costs when you buy from iRobot. Shipping is covered.",
      ),
      img: mockImage("https://irobot-api-mock.s3.amazonaws.com/box.png"),
    },
    {
      title: mockText("2-Year Warranty"),
      description: mockText(
        "We stand behind the design and craftsmanship of our air purifiers with a two-year limited warranty.",
      ),
      img: mockImage("https://irobot-api-mock.s3.amazonaws.com/ribon.png"),
    },
    {
      title: mockText("Monthly Payments"),
      description: mockText(
        "Every Klaara air purifier is affordable when priced through Affirm monthly financing. ",
      ),
      img: mockImage("https://irobot-api-mock.s3.amazonaws.com/affirm.png"),
    },
  ],

  showFooter: true,
  footer: {
    title: mockText(
      "Our air purifiers are rigorously tested and certified to perform at the highest level",
    ),
    logos: [
      mockImage(`${process.env.PUBLIC_URL}/assets/images/AHAM.png`),
      mockImage(`${process.env.PUBLIC_URL}/assets/images/EN1822.png`),
      mockImage(`${process.env.PUBLIC_URL}/assets/images/ISO.png`),
      mockImage(`${process.env.PUBLIC_URL}/assets/images/PTPA.png`),
      mockImage(
        `${process.env.PUBLIC_URL}/assets/images/CertifiedAsthmaAllergyFriendly.png`,
      ),
      mockImage(
        `${process.env.PUBLIC_URL}/assets/images/CRConsumerReports.png`,
      ),
      mockImage(`${process.env.PUBLIC_URL}/assets/images/CE.png`),
      mockImage(`${process.env.PUBLIC_URL}/assets/images/UL.png`),
      mockImage(`${process.env.PUBLIC_URL}/assets/images/ETLIntertek.png`),
      mockImage(`${process.env.PUBLIC_URL}/assets/images/EnergyStar.png`),
      mockImage(`${process.env.PUBLIC_URL}/assets/images/ARBCertified.png`),
      mockImage(`${process.env.PUBLIC_URL}/assets/images/CARB.png`),
    ],
  },
};

export default CHART_MOCKED_API_RESPONSE;
